import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Loader2, Link2, RefreshCw, ExternalLink, CheckCircle2, RotateCcw, Plug } from "lucide-react";
import CrmReadinessBadge from "@/components/CrmReadinessBadge";
import { EnterpriseConnectionStatus } from "@/components/EnterpriseCrm";
import AutomationActivity from "@/components/AutomationActivity";

const card = "bg-[#0E1624] rounded-3xl border border-[#1E2C45]";
const inputD = "bg-[#152033] border border-[#1E2C45] rounded-xl px-3 py-2 text-xs text-slate-200 placeholder:text-slate-500 outline-none focus:border-blue-500/50";
const SEV = { high: "bg-red-500/15 text-red-400", medium: "bg-amber-500/15 text-amber-400", low: "bg-slate-500/15 text-slate-400" };
const CRMS = ["filevine", "litify", "clio", "casepeer", "needles", "smartadvocate", "mycase", "practicepanther", "smokeball"];

export const CrmCaseWidget = ({ caseId, caseNumber }) => {
  const [d, setD] = useState(null);
  const [linking, setLinking] = useState(false);
  const [form, setForm] = useState({ provider: "filevine", matter_number: "", matter_url: "" });
  const load = useCallback(() => api.get(`/firm/command/crm/case/${caseId}`).then(({ data }) => setD(data)).catch(() => setD({})), [caseId]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />;
  const link = async () => {
    try {
      const { data } = await api.post(`/firm/command/crm/case/${caseId}/link-matter`, form);
      setD(data); setLinking(false); toast.success("Matter linked");
    } catch (e) { toast.error(e.response?.data?.detail || "Link failed"); }
  };
  return (
    <div className="rounded-2xl bg-[#0A111D] border border-[#1E2C45] p-3 text-[11px]" data-testid={`crm-widget-${caseId}`}>
      <div className="flex items-center justify-between">
        <span className="text-[9px] uppercase tracking-[0.2em] font-bold text-slate-500 flex items-center gap-1.5"><Plug className="w-3 h-3" /> Connected CRM</span>
        {d.connected ? <span className="text-emerald-400 font-bold flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Connected · {d.provider_name}</span>
          : d.matter_number ? <span className="text-blue-400 font-bold">Linked · {d.provider_name}</span>
          : <span className="text-slate-500">Not linked</span>}
      </div>
      {(d.matter_number || d.last_import_at) && (
        <div className="mt-1.5 text-slate-400 space-y-0.5">
          {d.matter_number && <div>Matter: <b className="text-slate-300">{d.matter_number}</b></div>}
          {d.last_import_at && <div>Last sync: {String(d.last_import_at).slice(0, 16).replace("T", " ")} · {d.jobs_count} job(s){d.keep_synced ? " · keep-synced" : ""}</div>}
          {d.stale && <div className="text-amber-400 font-semibold">⚠ Crash Ready has newer data than the CRM</div>}
        </div>
      )}
      {(d.comparison || []).map((c, i) => (
        <div key={i} className="mt-1.5 rounded-xl bg-[#152033] p-2" data-testid={`crm-compare-${i}`}>
          <div className="text-slate-300 font-semibold">{c.what_changed}</div>
          <div className="text-slate-500">{c.why_it_matters}</div>
          <div className="text-blue-400">→ {c.suggested_action}</div>
        </div>
      ))}
      <div className="mt-2 flex flex-wrap gap-1.5">
        {d.matter_url && (
          <a href={d.matter_url} target="_blank" rel="noreferrer" data-testid={`crm-open-matter-${caseId}`}
            className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-600 text-white text-[10px] font-bold"><ExternalLink className="w-3 h-3" /> Open Matter in CRM</a>
        )}
        <button onClick={() => setLinking(!linking)} data-testid={`crm-link-toggle-${caseId}`}
          className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#152033] border border-[#1E2C45] text-slate-300 text-[10px] font-bold"><Link2 className="w-3 h-3" /> {d.matter_number ? "Update Link" : "Link Matter"}</button>
        <button onClick={load} className="p-1 rounded-full text-slate-500 hover:text-blue-400"><RefreshCw className="w-3 h-3" /></button>
      </div>
      {linking && (
        <div className="mt-2 grid grid-cols-2 gap-1.5" data-testid={`crm-link-form-${caseId}`}>
          <select value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} data-testid="crm-link-provider" className={inputD}>
            {CRMS.map((c) => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
          </select>
          <input value={form.matter_number} onChange={(e) => setForm({ ...form, matter_number: e.target.value })} placeholder="Matter number *" data-testid="crm-link-matter" className={inputD} />
          <input value={form.matter_url} onChange={(e) => setForm({ ...form, matter_url: e.target.value })} placeholder="https:// matter deep link (optional)" data-testid="crm-link-url" className={`${inputD} col-span-2`} />
          <button onClick={link} data-testid="crm-link-save" className="col-span-2 px-3 py-1.5 rounded-full bg-blue-600 text-white text-[10px] font-bold">Save Link</button>
        </div>
      )}
      <p className="mt-2 text-[9px] text-slate-600">{d.note}</p>
    </div>
  );
};

const YOUR_CRM = ["Contacts", "Calendar", "Notes", "Tasks", "Documents", "Billing", "Internal Workflow"];
const CR_ADDS = ["RN Triage Intelligence", "Drone Deployment Network", "Medical Command Center", "AI Case Timeline",
  "Treatment Gap Detection", "Demand Readiness", "Settlement Readiness", "Body Mapping", "Medical Records Tracking",
  "Provider Network", "AI Evidence Intelligence", "Case Health Monitoring", "Litigation Export Engine",
  "Intelligent Alerts", "Operational Dashboards"];

const ReadinessView = () => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/firm/command/readiness").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return <Loader2 className="w-5 h-5 text-blue-500 animate-spin mx-auto my-6" />;
  return (
    <div className="space-y-3" data-testid="crm-readiness-view">
      <div className={`${card} p-5 flex flex-wrap items-center gap-5`}>
        <div>
          <div className="text-4xl font-extrabold text-white font-heading" data-testid="firm-readiness-score">{d.score}%</div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">CRM Readiness</div>
        </div>
        <div className="text-5xl font-extrabold text-emerald-400 font-heading" data-testid="firm-readiness-grade">{d.grade}</div>
        <div className="flex-1 min-w-[240px] grid sm:grid-cols-2 gap-1">
          {d.breakdown.map((b) => (
            <div key={b.key} className="text-[11px]" data-testid={`readiness-${b.key}`}>
              <span className={b.enabled ? "text-emerald-400" : "text-slate-600"}>{b.enabled ? "✓" : "○"}</span>
              <span className={`ml-1.5 ${b.enabled ? "text-slate-300" : "text-slate-500"}`}>{b.label}</span>
              <span className="text-slate-600 ml-1">+{b.points}</span>
            </div>
          ))}
        </div>
      </div>
      {d.recommendations.length > 0 && (
        <div className={`${card} p-4 text-[11px]`} data-testid="readiness-recommendations">
          <div className="text-xs font-semibold text-white mb-1.5">Improve your score</div>
          {d.recommendations.map((r, i) => <div key={i} className="text-amber-400/90">→ {r}</div>)}
        </div>
      )}
      <div className={`${card} p-5`} data-testid="crm-comparison-panel">
        <div className="text-sm font-bold text-white">{d.tagline} <span className="text-[10px] text-slate-500 font-normal ml-2">Crash Ready extends your CRM. It does not replace it.</span></div>
        <div className="mt-3 grid sm:grid-cols-2 gap-4 text-[11px]">
          <div>
            <div className="text-[9px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-1.5">Your CRM Handles</div>
            {YOUR_CRM.map((x) => <div key={x} className="text-slate-400 py-0.5">• {x}</div>)}
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-[0.2em] font-bold text-blue-400 mb-1.5">Crash Ready Adds</div>
            <div className="flex flex-wrap gap-1">
              {CR_ADDS.map((x) => <span key={x} className="px-2 py-0.5 rounded-full bg-blue-500/10 border border-blue-500/25 text-blue-300 text-[10px]">✓ {x}</span>)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CompatibilityView = () => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/firm/command/compatibility").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return <Loader2 className="w-5 h-5 text-blue-500 animate-spin mx-auto my-6" />;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="crm-compatibility">
      {d.crms.map((c) => (
        <div key={c.key} className={`${card} p-4`} data-testid={`compat-${c.key}`}>
          <div className="flex items-center justify-between">
            <span className="text-sm font-bold text-white">{c.name}</span>
            <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase ${c.status === "Live" ? "bg-emerald-500/15 text-emerald-400" : "bg-blue-500/15 text-blue-400"}`}>{c.status}</span>
          </div>
          <div className="mt-1.5 text-[10px] text-slate-500 space-y-0.5">
            <div>Connection: <b className={c.connection === "connected" ? "text-emerald-400" : "text-slate-400"}>{c.connection.replace(/_/g, " ")}</b>{c.mode ? ` (${c.mode})` : ""}</div>
            <div>Auth: {c.auth_type} · Setup: {c.estimated_setup}</div>
            <div className="truncate">Objects: {c.supported_objects.join(", ")}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

const ValueView = () => {
  const [v, setV] = useState(null);
  const [n, setN] = useState(null);
  useEffect(() => {
    api.get("/firm/command/value").then(({ data }) => setV(data)).catch(() => {});
    api.get("/firm/command/network").then(({ data }) => setN(data)).catch(() => {});
  }, []);
  if (!v) return <Loader2 className="w-5 h-5 text-blue-500 animate-spin mx-auto my-6" />;
  return (
    <div className="space-y-4" data-testid="crm-value-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
        {v.metrics.map((m) => (
          <div key={m.key} className={`${card} p-3`} data-testid={`value-${m.key}`}>
            <div className="text-xl font-extrabold text-white font-heading">{m.value}</div>
            <div className="text-[9px] uppercase tracking-wide text-slate-500 font-semibold leading-tight">{m.label}</div>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-slate-600">{v.note}</p>
      {n && (
        <div className={`${card} p-4`} data-testid="network-intelligence">
          <div className="text-xs font-semibold text-white mb-2">Crash Ready Network Intelligence</div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            {n.metrics.map((m) => (
              <div key={m.key} className="rounded-xl bg-[#152033] p-2.5" data-testid={`network-${m.key}`}>
                <div className="text-lg font-extrabold text-white font-heading">{m.value}</div>
                <div className="text-[9px] uppercase tracking-wide text-slate-500 font-semibold">{m.label}</div>
              </div>
            ))}
          </div>
          <p className="mt-2 text-[9px] text-slate-600">{n.note}</p>
        </div>
      )}
    </div>
  );
};

export const CrmCommand = () => {
  const [tab, setTab] = useState("health");
  const [health, setHealth] = useState(null);
  const [sync, setSync] = useState(null);
  const [period, setPeriod] = useState("all");
  const loadHealth = useCallback(() => api.get("/firm/command/crm/health").then(({ data }) => setHealth(data)).catch(() => setHealth({ issues: [] })), []);
  useEffect(() => { loadHealth(); }, [loadHealth]);
  useEffect(() => {
    if (tab === "sync") api.get(`/firm/command/crm/sync-center?period=${period}`).then(({ data }) => setSync(data)).catch(() => setSync({}));
  }, [tab, period]);
  const resolve = async (i, reopen = false) => {
    try {
      await api.post(`/firm/command/crm/health/${i.issue_id}/${reopen ? "reopen" : "resolve"}`);
      toast.success(reopen ? "Reopened" : "Marked resolved"); loadHealth();
    } catch { toast.error("Failed"); }
  };
  return (
    <div className="space-y-4" data-testid="crm-command">
      <CrmReadinessBadge dark />
      <EnterpriseConnectionStatus dark />
      <AutomationActivity dark />
      <div className="flex gap-1.5 flex-wrap">
        {[["health", "CRM Health"], ["sync", "Sync Center"], ["readiness", "Readiness Score"], ["compat", "Compatibility"], ["value", "Value & Network"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`crm-tab-${k}`}
            className={`px-3.5 py-1.5 rounded-full text-xs font-semibold ${tab === k ? "bg-blue-600 text-white" : "bg-[#0E1624] border border-[#1E2C45] text-slate-400"}`}>{l}</button>
        ))}
        {tab === "sync" && (
          <span className="ml-auto flex gap-1">
            {["today", "week", "month", "all"].map((p) => (
              <button key={p} onClick={() => setPeriod(p)} data-testid={`crm-period-${p}`}
                className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${period === p ? "bg-blue-600 text-white" : "bg-[#0E1624] border border-[#1E2C45] text-slate-500"}`}>{p}</button>
            ))}
          </span>
        )}
      </div>

      {tab === "readiness" && <ReadinessView />}
      {tab === "compat" && <CompatibilityView />}
      {tab === "value" && <ValueView />}

      {tab === "health" && (!health ? <Loader2 className="w-6 h-6 text-blue-500 animate-spin mx-auto my-8" /> : (
        <div className={`${card} p-5`} data-testid="crm-health">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-white">CRM Health Monitor <span className="text-red-400 ml-1">{health.open} open</span> <span className="text-emerald-400 ml-1">{health.resolved} resolved</span></span>
          </div>
          <p className="mt-1 text-[10px] text-slate-500">{health.note}</p>
          <div className="mt-3 space-y-1.5 max-h-[26rem] overflow-y-auto pr-1">
            {(health.issues || []).map((i) => (
              <div key={i.issue_id} className={`rounded-xl bg-[#152033] px-3 py-2 text-[11px] flex flex-wrap items-center gap-2 ${i.status === "resolved" ? "opacity-50" : ""}`} data-testid={`crm-issue-${i.code}-${i.case_id}`}>
                <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${SEV[i.severity]}`}>{i.severity}</span>
                <span className="text-slate-500 font-semibold">{i.case_number}</span>
                <span className="flex-1 min-w-[200px] text-slate-300">{i.description}<span className="block text-blue-400/80">→ {i.recommended_fix}</span></span>
                {i.status === "open" ? (
                  <button onClick={() => resolve(i)} data-testid={`crm-resolve-${i.issue_id}`}
                    className="px-2.5 py-1 rounded-full bg-emerald-600 text-white text-[10px] font-bold">Resolve</button>
                ) : (
                  <span className="flex items-center gap-1.5 text-emerald-400 text-[10px]">Resolved by {i.resolved_by}
                    <button onClick={() => resolve(i, true)} data-testid={`crm-reopen-${i.issue_id}`} className="p-1 text-slate-500 hover:text-amber-400"><RotateCcw className="w-3 h-3" /></button></span>
                )}
              </div>
            ))}
            {(health.issues || []).length === 0 && <p className="text-xs text-slate-500 py-4" data-testid="crm-health-clear">All synchronized — no health issues detected on your active cases.</p>}
          </div>
        </div>
      ))}

      {tab === "sync" && (!sync ? <Loader2 className="w-6 h-6 text-blue-500 animate-spin mx-auto my-8" /> : (
        <div className="space-y-3" data-testid="crm-sync-center">
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-2">
            {[["Connections", (sync.connections || []).filter((c) => c.status === "connected").length],
              ["Jobs", sync.stats?.total_jobs || 0], ["Success", sync.stats?.success || 0],
              ["Failed", sync.stats?.failed || 0], ["Imports", sync.stats?.imports || 0],
              ["Webhook Events", sync.stats?.webhook_events || 0]].map(([l, v]) => (
              <div key={l} className={`${card} p-3`}><div className="text-lg font-extrabold text-white font-heading">{v}</div>
                <div className="text-[9px] uppercase tracking-wide text-slate-500 font-semibold">{l}</div></div>
            ))}
          </div>
          <div className={`${card} p-4`}>
            <div className="text-xs font-semibold text-white mb-2">Connected Systems</div>
            {(sync.connections || []).map((c) => (
              <div key={c.provider} className="flex items-center gap-2 text-[11px] py-1 border-b border-[#152033] last:border-0">
                <span className={`h-1.5 w-1.5 rounded-full ${c.status === "connected" ? "bg-emerald-500" : "bg-slate-600"}`} />
                <span className="flex-1 text-slate-300 capitalize">{c.provider} <span className="text-slate-500">({c.mode})</span></span>
                <span className="text-slate-500">{c.last_sync_at ? `last sync ${String(c.last_sync_at).slice(0, 16).replace("T", " ")}` : "never synced"}</span>
                {c.last_error && <span className="text-red-400 truncate max-w-[200px]">{c.last_error}</span>}
              </div>
            ))}
            {(sync.connections || []).length === 0 && <p className="text-[11px] text-slate-500">No CRM connected yet — connect one from the case file CRM panel (Cases view).</p>}
          </div>
          <div className={`${card} p-4`}>
            <div className="text-xs font-semibold text-white mb-2">Sync Activity & Audit History</div>
            <div className="space-y-1 max-h-64 overflow-y-auto text-[10px]">
              {(sync.logs || []).map((l) => (
                <div key={l.log_id} className="flex gap-2 text-slate-400">
                  <span className="text-slate-600 shrink-0 w-28">{String(l.created_at).slice(0, 16).replace("T", " ")}</span>
                  <span className={`shrink-0 uppercase font-bold ${l.level === "error" ? "text-red-400" : "text-slate-500"}`}>{l.level}</span>
                  <span className="capitalize shrink-0 text-slate-500">{l.provider}</span>
                  <span className="truncate">{l.message}</span>
                </div>
              ))}
              {(sync.logs || []).length === 0 && <p className="text-slate-500">No sync activity in this period.</p>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default CrmCommand;
